"use client";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Image, { StaticImageData } from "next/image";
import { Icon } from "@iconify/react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import gsap from "gsap";
import { Menu, X } from "lucide-react";
import Button from "./ui/button";
import { useCollections } from "@/context/Collections";
import { useActivities } from "@/context/Activities";
import { useRouter } from "next/navigation";
import LanguageSelector from "./LanguageSelector";

interface Package {
  packageName: string;
  packageImg: StaticImageData;
  packageDesc: string;
  packageDay: string;
}

interface Expedition {
  name: string;
  maxElevation?: string;
  banner?: string;
  package?: Package[];
  expeditions?: Expedition[];
  categoryId?: string;
  slug?: string;
  description?: string;
  image?: string;
  _id?: string;
  createdAt?: string;
  updatedAt?: string;
  heightCategoryId?: string;
}

interface Category {
  id: string;
  name: string;
  description?: string;
}

interface NavLink {
  id: number;
  name: string;
  href: string;
}

interface CompanyLink {
  name: string;
  href: string;
}

const NavLinks: NavLink[] = [
  { id: 1, name: "Home", href: "/" },
  { id: 2, name: "Mountaineering", href: "/expedition" },
  { id: 3, name: "Trekking", href: "/trek" },
  { id: 4, name: "Services", href: "" },
  { id: 5, name: "Company", href: "" },
  { id: 6, name: "Training", href: "" },
  { id: 7, name: "Calendar", href: "/calendar" },
  { id: 8, name: "Blogs", href: "/blogs" },
  { id: 9, name: "Contact us", href: "/contact_us" },
];

const CompanyLinks: CompanyLink[] = [
  { name: "About Us", href: "/about_us" },
  { name: "Our team", href: "/our_team" },
  { name: "Message from CEO", href: "/message_from_ceo" },
  { name: "Certificates", href: "/certificates" },
  { name: "Become partner with us", href: "/partner" },
  {
    name: "Useful Information",
    href: "/useful_info/gear-and-equipment-of-trekking-and-mountaineering",
  },
  { name: "Terms & Conditions", href: "/terms" },
];

export default function Nav() {
  const router = useRouter();
  const { collections, collection, loading, error } = useCollections();
  // data for activies i.e services link
  const { activities } = useActivities();

  const [isOpenmountain, setIsOpenmountain] = useState(false);
  const [isOpentrek, setIsOpentrek] = useState(false);
  const [isOpencompany, setIsOpencompany] = useState(false);
  const [isOpenservice, setIsOpenservice] = useState(false);
  const [isOpentraining, setIsOpentraining] = useState(false);
  const [isRotatedmountain, setIsRotatedmountain] = useState(false);
  const [isRotatedtrek, setIsRotatedtrek] = useState(false);
  const [isRotatedcompany, setIsRotatedcompany] = useState(false);
  const [isRotatedservice, setIsRotatedservice] = useState(false);
  const [isRotatedtraining, setIsRotatedtraining] = useState(false);

  const toggleDropdownmountain = () => {
    setIsOpenmountain(!isOpenmountain);
    setIsRotatedmountain(!isRotatedmountain);
    setIsOpentrek(false);
    setIsOpencompany(false);
    setIsOpenservice(false);
    setIsOpentraining(false);
    setIsRotatedtraining(false);
    setIsRotatedtrek(false);
    setIsRotatedcompany(false);
    setIsRotatedservice(false);
  };
  const toggleDropdowntrek = () => {
    setIsOpentrek(!isOpentrek);
    setIsRotatedtrek(!isRotatedtrek);
    setIsOpenmountain(false);
    setIsOpencompany(false);
    setIsOpenservice(false);
    setIsOpentraining(false);
    setIsRotatedtraining(false);
    setIsRotatedmountain(false);
    setIsRotatedcompany(false);
    setIsRotatedservice(false);
  };

  const toggleDropdowncompany = () => {
    setIsOpencompany(!isOpencompany);
    setIsRotatedcompany(!isRotatedcompany);
    setIsOpentrek(false);
    setIsOpenmountain(false);
    setIsOpenservice(false);
    setIsOpentraining(false);
    setIsRotatedtraining(false);
    setIsRotatedtrek(false);
    setIsRotatedmountain(false);
    setIsRotatedservice(false);
  };
  const toggleDropdownservice = () => {
    setIsOpenservice(!isOpenservice);
    setIsRotatedservice(!isRotatedservice);
    setIsOpentrek(false);
    setIsOpencompany(false);
    setIsOpenmountain(false);
    setIsOpentraining(false);
    setIsRotatedtraining(false);
    setIsRotatedtrek(false);
    setIsRotatedcompany(false);
    setIsRotatedmountain(false);
  };
  const toggleDropdowntraining = () => {
    setIsOpentraining(!isOpentraining);
    setIsRotatedtraining(!isRotatedtraining);
    setIsOpentrek(false);
    setIsOpencompany(false);
    setIsOpenmountain(false);
    setIsRotatedtrek(false);
    setIsRotatedcompany(false);
    setIsRotatedmountain(false);
  };

  const currentRoute = usePathname();
  const pathname = usePathname();

  const [isScrolled, setIsScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [navHover, setNavhover] = useState(false);
  const [openCompany, setOpenCompany] = useState(false);
  const [openTraining, setOpenTraining] = useState(false);
  const [openServices, setOpenServices] = useState(false);
  const [hoveredData, setHoveredData] = useState<Expedition[] | undefined>([]);
  const [hoveredPackage, setHoveredPackage] = useState<string>("");
  const [hoveredLink, setHoveredLink] = useState<string>("");
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  useEffect(() => {
    // Close all dropdowns and mobile nav when route changes
    setIsMobileNavOpen(false);
    setIsOpenmountain(false);
    setIsOpentrek(false);
    setIsOpencompany(false);
    setIsOpentraining(false);
    setIsOpenservice(false);
    setOpenMenu(false);
    setOpenCompany(false);
    setOpenTraining(false);
    setOpenServices(false);
    setNavhover(false);
  }, [pathname]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);

  const handleHoverLink = (link: string) => {
    setNavhover(true);
    setHoveredLink(link);

    if (["Mountaineering", "Trekking", "Other Activities"].includes(link)) {
      setOpenMenu(true);
      setOpenCompany(false);
      setOpenServices(false);
      setOpenTraining(false);
      const links = link.toLowerCase();
      const linkData = organizedData[links as keyof typeof organizedData];
      const lastItem = linkData[0];
      setHoveredData(lastItem?.expeditions ?? []);
    } else if (
      link === "Company" ||
      currentRoute.includes("/aboutus") ||
      currentRoute.includes("/ourteamss") ||
      currentRoute.includes("/messagefromceo") ||
      currentRoute.includes("/ourteam")
    ) {
      setOpenCompany(true);
      setOpenTraining(false);
      setOpenServices(false);
      setOpenMenu(false);
    } else if (
      link === "Training" ||
      currentRoute.includes("/mountaineeringsssss") ||
      currentRoute.includes("/trekkingsss")
    ) {
      setOpenCompany(false);
      setOpenTraining(true);
      setOpenServices(false);
      setOpenMenu(false);
    } else if (
      link === "Services" ||
      currentRoute.includes("/other_activities/raftings") ||
      currentRoute.includes("/other_activities/heli_tours") ||
      currentRoute.includes("/other_activities/paraglidings") ||
      currentRoute.includes("/other_activities/rock_climbings") ||
      currentRoute.includes("/other_activities/tours") ||
      currentRoute.includes("/other_activities/nepaltours") ||
      currentRoute.includes("/other_activities/safaris") ||
      currentRoute.includes("/other_activities/iceclimbings") ||
      currentRoute.includes("/other_activities/schooling_gsroup") ||
      currentRoute.includes("/other_activities/mountain_bikings")
    ) {
      setOpenCompany(false);
      setOpenTraining(false);
      setOpenServices(true);
      setOpenMenu(false);
    } else {
      setOpenMenu(false);
      setOpenCompany(false);
      setOpenTraining(false);
      setOpenServices(false);
    }

    switch (link) {
      case "Mountaineering":
        return setCurrentPackages(organizedData?.mountaineering);
      case "Trekking":
        return setCurrentPackages(organizedData?.trekking);
      case "Other Activities":
        return setHoveredData([]);
      default:
        return setHoveredData([]);
    }
  };

  const [currentPackages, setCurrentPackages] = useState<Expedition[]>([]);

  const [organizedData, setOrganizedData] = useState<{
    [key: string]: Expedition[];
  }>({
    mountaineering: [],
    trekking: [],
  });

  useEffect(() => {
    if (!collections || !collections.length) return;

    const categoryMap = new Map();

    collections.forEach((item) => {
      if (!item.collections) return;

      const collectionName = item.collections.name.toLowerCase();

      if (!categoryMap.has(collectionName)) {
        categoryMap.set(collectionName, new Map());
      }

      const categoriesInCollection = categoryMap.get(collectionName);

      if (!categoriesInCollection.has(item.categoryId)) {
        categoriesInCollection.set(item.categoryId, {
          id: item.categoryId,
          name: item.name,
          slug: item.slug,
          description: item.description,
          image: item.image,
          expeditions: item.expeditions,
          _id: item._id,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        });
      }
    });

    const organized: { [key: string]: Expedition[] } = {};
    categoryMap.forEach((categories, collectionName) => {
      organized[collectionName] = Array.from(categories.values());
    });

    setOrganizedData(organized);
  }, [collections]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Check the pathname
    if (
      pathname.startsWith("/expedition") ||
      pathname.startsWith("/trek") ||
      pathname.startsWith("/customizetrip") ||
      pathname.startsWith("/partner") ||
      pathname.startsWith("/detailspage") ||
      pathname.startsWith("/blog_detail") ||
      pathname.startsWith("/calendar") ||
      pathname.startsWith("/blogs") ||
      pathname.startsWith("/booking") ||
      pathname.startsWith("/contact_us") ||
      pathname.startsWith("/other_activities")
    ) {
      // Set background to black
      gsap.to(".navbar", {
        backgroundColor: "#000000",
        duration: 0.5,
      });
    } else {
      gsap.to(".navbar", {
        backgroundColor: isScrolled ? "#1E1E1E" : "",
        duration: 0.5,
      });
    }
  }, [isScrolled, pathname]);

  // Mobile Navigation Animations
  const mobileNavVariants = {
    closed: {
      x: "100%",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
  };

  const linkVariants = {
    closed: { opacity: 0, y: 20 },
    open: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    }),
  };

  return (
    <div
      onMouseLeave={() => {
        setOpenCompany(false);
        setOpenTraining(false);
        setOpenServices(false);
        setOpenMenu(false);
        setNavhover(false);
        setHoveredData([]);
      }}
      className={`navbar w-full z-[999] ${
        navHover ||
        currentRoute.includes("/message_from_ceo") ||
        currentRoute.includes("/blogs/") ||
        currentRoute.includes("/profile/")
          ? "bg-[#1E1E1E]"
          : "bg-transparent"
      } origin-top w-full flex fixed top-0 left-0 z-[100] h-[4rem]`}
    >
      <div className="w-11/12 text-sm  flex justify-between items-center mx-auto h-full">
        {openMenu && (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onMouseLeave={() => {
                setOpenMenu(false);
                setNavhover(false);
              }}
              className="absolute border-zinc-700 border-opacity-[0.5] w-full mx-auto z-10 bg-[#1E1E1E] shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),0px_1px_0px_0px_rgba(25,28,33,0.02),0px_0px_0px_1px_rgba(25,28,33,0.08)] top-[100%] origin-top left-0 text-zinc-50"
            >
              <div className="w-11/12 mx-auto grid grid-cols-6">
                <motion.div
                  initial={{ opacity: 0, marginTop: "20px" }}
                  animate={{ opacity: 1, marginTop: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col justify-between h-full border-zinc-700 border-opacity-[0.5] py-5 gap-2"
                >
                  <div className="flex flex-col  gap-2">
                    {currentPackages.map((item, index) => (
                      <Link
                        key={index}
                        href={
                          item.name
                            ? `/${
                                hoveredLink === "Mountaineering"
                                  ? "expedition"
                                  : hoveredLink === "Trekking"
                                    ? "trek"
                                    : hoveredLink === "Other Activities"
                                      ? "other_activities"
                                      : ""
                              }/#${item.name}` // Include hash for section ID`
                            : ""
                        }
                        onMouseEnter={() => setHoveredData(item.expeditions)}
                        className={`text-left text-[15px] cursor-pointer hover:text-orange-500 ${
                          hoveredPackage === item.name
                            ? "text-orange-500"
                            : "text-zinc-300"
                        }`}
                        style={{}}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>

                  {/* view all button */}
                  {currentPackages.slice(0, 1).map((item, index) => (
                    <Link
                      key={index}
                      href={
                        item.name
                          ? `/${
                              hoveredLink === "Mountaineering"
                                ? "expedition"
                                : hoveredLink === "Trekking"
                                  ? "trek"
                                  : ""
                            }`
                          : ""
                      }
                      onMouseEnter={() => setHoveredData(item.expeditions)}
                      className={`text-left text-[15px] cursor-pointer flex items-center gap-2 hover:text-orange-500 ease-in-out duration-200 `}
                    >
                      <span className="font-semibold">View All</span>
                      <Icon
                        icon="lucide:circle-arrow-right"
                        className="w-8 h-8 text-orange-500"
                      />
                    </Link>
                  ))}
                </motion.div>

                <div className="flex w-full col-span-5 flex-col gap-2 py-5 h-full">
                  <div className="grid grid-cols-4 gap-2">
                    {hoveredData?.slice(0, 8).map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, marginTop: "20px" }}
                        animate={{ opacity: 1, marginTop: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="flex overflow-hidden rounded-md flex-col gap-2 min-w-full relative h-[18rem] group cursor-pointer"
                      >
                        <Link
                          href={`/package_detail/${item.slug}`}
                          className="overflow-hidden group  absolute font-semibold text-center top-1/2 left-1/2 mx-auto w-full z-10 mt-[5%]  opacity-[1] text-white -translate-x-1/2 -translate-y-1/2 duration-200"
                        >
                          <p className="text-lg font-medium group-hover:text-orange-500 line-clamp-1">
                            {item.name}
                          </p>
                          <p className="group-hover:text-orange-500">
                            {item.maxElevation}
                          </p>
                        </Link>
                        <Link
                          href={`/package_detail/${item.slug}`}
                          className="w-full h-full  group-hover:scale-110 ease-in-out duration-300 group-hover:brightness-[0.5]"
                        >
                          {item.banner && (
                            <Image
                              alt="package-img"
                              src={item.banner}
                              width={500}
                              height={500}
                              className="object-cover object-center brightness-[0.5] duration-200 w-full h-full  "
                            />
                          )}
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        )}
        <Link href="/" className="">
          <Image
            src={
              isScrolled ? "/logo/infinity-logo.png" : "/logo/infinity-logo.png"
            }
            width={96}
            height={12}
            className="w-[8rem]"
            alt="logo"
            priority
          />
        </Link>
        <div className="hidden xl:flex gap-5 relative justify-center items-center">
          {NavLinks.map((link, index) => (
            <div
              onClick={(e) => {
                router.push(link.href);
                e.stopPropagation();
              }}
              key={index}
              className="relative"
            >
              <span
                onMouseEnter={() => handleHoverLink(link.name)}
                className={`nav-links ${
                  currentRoute === link.href
                    ? "text-orange-500"
                    : "text-zinc-300"
                } font-semibold items-center hover:text-orange-500 cursor-pointer flex gap-1`}
              >
                {link.name}
                {!["Home", "Contact us", "Blogs", "Calendar"].includes(
                  link.name,
                ) && <Icon icon="iconamoon:arrow-down-2" />}
              </span>

              {link.name === "Company" && openCompany && (
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onMouseLeave={() => setOpenCompany(false)}
                    className="absolute text-nowrap gap-3 bg-[#1E1E1E] flex flex-col shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),0px_1px_0px_0px_rgba(25,28,33,0.02),0px_0px_0px_1px_rgba(25,28,33,0.08)] justify-center items-start px-5 pb-4 top-[100%] mt-[1.2rem] origin-top left-0 text-zinc-800"
                  >
                    {CompanyLinks.map((link, index) => (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          router.push(link.href);
                        }}
                        key={index}
                        className="text-left hover:text-orange-500 text-zinc-300 text-[15px] cursor-pointer"
                      >
                        {link.name}
                      </div>
                    ))}
                  </motion.div>
                </AnimatePresence>
              )}

              {link.name === "Training" && openTraining && (
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onMouseLeave={() => setOpenTraining(false)}
                    className="absolute text-nowrap gap-3 bg-[#1E1E1E] flex flex-col shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),0px_1px_0px_0px_rgba(25,28,33,0.02),0px_0px_0px_1px_rgba(25,28,33,0.08)] justify-center items-start px-5 pb-4 top-[100%] mt-[1.2rem] origin-top left-0 text-zinc-800"
                  >
                    {organizedData?.training &&
                      organizedData.training.length &&
                      organizedData?.training[0]?.expeditions?.map(
                        (link, index) => (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              router.push(`/package_detail/${link.slug}`);
                            }}
                            key={index}
                            className="text-left hover:text-orange-500 text-zinc-300 text-[15px] cursor-pointer"
                          >
                            {link.name}
                          </div>
                        ),
                      )}
                  </motion.div>
                </AnimatePresence>
              )}

              {link.name === "Services" && openServices && (
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onMouseLeave={() => setOpenServices(false)}
                    className="absolute text-nowrap gap-3 bg-[#1E1E1E] flex flex-col shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),0px_1px_0px_0px_rgba(25,28,33,0.02),0px_0px_0px_1px_rgba(25,28,33,0.08)] justify-center items-start p-5 top-[100%] mt-[1.2rem] origin-top left-0 text-zinc-800"
                  >
                    {collections
                      ?.filter((item) => item.collections.slug === "services")
                      .map((item, index) => (
                        <div
                          onClick={(e) => {
                            router.push(`/other_activities/${item.slug}`);
                            e.stopPropagation();
                          }}
                          key={index}
                          className="text-left hover:text-orange-500 text-zinc-300 text-[15px] cursor-pointer"
                        >
                          {item.name}
                        </div>
                      ))}
                  </motion.div>
                </AnimatePresence>
              )}
            </div>
          ))}

          <div className="flex items-center gap-2.5">
            <Link href="/customizetrip">
              <Button className="!h-[38px] !px-4 !py-0 !text-xs font-semibold flex items-center justify-center">
                Customize Your Trip
              </Button>
            </Link>

            <LanguageSelector variant="desktop" />
          </div>
        </div>
        <div className="flex items-center gap-3 xl:hidden">
          <LanguageSelector variant="desktop" />
          <Menu
            className="text-slate-50 cursor-pointer"
            onClick={() => setIsMobileNavOpen(true)}
          />
        </div>

        {/* Mobile Side Nav */}
        <AnimatePresence>
          {isMobileNavOpen && (
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={mobileNavVariants}
              className="xl:hidden fixed top-0 right-0 h-screen overflow-y-scroll w-full bg-[#1E1E1E] z-[9999] overflow-hidden"
            >
              <motion.div
                className="p-5 flex justify-between items-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Image
                  src="/logo/favicon.png"
                  width={96}
                  height={12}
                  className="w-[2rem]"
                  alt="Logo"
                />
                <X
                  className="text-slate-50 cursor-pointer"
                  onClick={() => setIsMobileNavOpen(false)}
                />
              </motion.div>

              <motion.div className="flex flex-col gap-4 p-5">
                <nav className="text-white font-semibold text-xl space-y-4">
                  <Link href="/">
                    <h2>Home</h2>
                  </Link>
                  <div className="border-b border-white/50 pb-2">
                    <div
                      className="flex justify-between items-center"
                      onClick={toggleDropdownmountain}
                    >
                      <h2 className="cursor-pointer">Mountaineering</h2>
                      <Image
                        src="/drop.png"
                        alt="mountain"
                        width={200}
                        height={200}
                        className={`h-6 w-6 transition-transform duration-500 ${
                          isRotatedmountain ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                    <div
                      className={`overflow-hidden transition-max-height duration-500 ease-in-out ${
                        isOpenmountain ? "max-h-96" : "max-h-0"
                      }`}
                    >
                      <div className="pl-4 mt-2 text-[16px] flex flex-col">
                        {organizedData?.mountaineering?.map((item, index) => {
                          return (
                            <Link
                              href={`/expedition/#${item.name}`}
                              key={index}
                            >
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="border-b border-white/50 pb-2">
                    <div
                      className="flex justify-between items-center"
                      onClick={toggleDropdowntrek}
                    >
                      <h2 className="cursor-pointer">Trekking</h2>
                      <Image
                        src="/drop.png"
                        alt="mountain"
                        width={200}
                        height={200}
                        className={`h-6 w-6 transition-transform duration-500 ${
                          isRotatedtrek ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                    <div
                      className={`overflow-hidden transition-max-height duration-500 ease-in-out ${
                        isOpentrek ? "max-h-96" : "max-h-0"
                      }`}
                    >
                      <div className="pl-4 mt-2 text-[16px] flex flex-col">
                        {organizedData?.trekking?.map((item, index) => {
                          return (
                            <Link href={`/trek/#${item.name}`} key={index}>
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="border-b border-white/50 pb-2">
                    <div
                      className="flex justify-between items-center"
                      onClick={toggleDropdowncompany}
                    >
                      <h2 className="cursor-pointer">Company</h2>
                      <Image
                        src="/drop.png"
                        alt="mountain"
                        width={200}
                        height={200}
                        className={`h-6 w-6 transition-transform duration-500 ${
                          isRotatedcompany ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                    <div
                      className={`overflow-hidden transition-max-height duration-500 ease-in-out ${
                        isOpencompany ? "max-h-96" : "max-h-0"
                      }`}
                    >
                      <div className="pl-4 mt-2 text-[16px]">
                        {aboutlinks.map((item, index) => (
                          <div key={index}>
                            <Link href={item.path}>{item.name}</Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="border-b border-white/50 pb-2">
                    <div
                      className="flex justify-between items-center"
                      onClick={toggleDropdownservice}
                    >
                      <h2 className="cursor-pointer">Services</h2>
                      <Image
                        src="/drop.png"
                        alt="mountain"
                        width={200}
                        height={200}
                        className={`h-6 w-6 transition-transform duration-500 ${
                          isRotatedservice ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                    <div
                      className={`overflow-hidden transition-max-height duration-500 ease-in-out ${
                        isOpenservice ? "max-h-96" : "max-h-0"
                      }`}
                    >
                      <div className="pl-4 mt-2 text-[16px]">
                        <div className="pl-4 mt-2 text-[16px] flex flex-col">
                          {activities?.map((item, index) => {
                            return (
                              <Link
                                href={`/other_activities/${item.slug}` || "/"}
                                key={index}
                              >
                                {item.name}
                              </Link>
                            );
                          })}
                          {collections
                            .filter((item, index) => {
                              return item.collections.slug === "services";
                            })
                            .slice(0, 10)
                            .map((item, index) => (
                              <div key={index} className="">
                                <Link href={`/trek/${item._id} `}>
                                  <h2 className="">{item.name}</h2>
                                </Link>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border-b border-white/50 pb-2">
                    <div
                      className="flex justify-between items-center"
                      onClick={toggleDropdowntraining}
                    >
                      <h2 className="cursor-pointer">Training</h2>
                      <Image
                        src="/drop.png"
                        alt="mountain"
                        width={200}
                        height={200}
                        className={`h-6 w-6 transition-transform duration-500 ${
                          isRotatedtraining ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                    <div
                      className={`overflow-hidden transition-max-height duration-500 ease-in-out ${
                        isOpentraining ? "max-h-96" : "max-h-0"
                      }`}
                    >
                      <div className="pl-4 mt-2 text-[16px] flex flex-col">
                        {organizedData?.training &&
                          organizedData.training.length &&
                          organizedData?.training[0]?.expeditions?.map(
                            (item, index) => {
                              return (
                                <Link
                                  href={`/training/himalayan-mountaineering-course`}
                                  key={index}
                                >
                                  {item.name}
                                </Link>
                              );
                            },
                          )}
                      </div>
                    </div>
                  </div>

                  <Link href="/calendar">
                    <h2 className="border-b border-white/50 pb-2 mt-2">
                      Calendar
                    </h2>
                  </Link>
                  <Link href="/blogs">
                    <h2 className="border-b border-white/50 pb-2 mt-2">
                      Blogs
                    </h2>
                  </Link>
                  <Link href="/contact_us">
                    <h2 className="border-b border-white/50 pb-2 mt-2">
                      Contact
                    </h2>
                  </Link>
                  <Link href="/customizetrip">
                    <Button className="mt-4 w-full">Customize your Trip</Button>
                  </Link>
                  <LanguageSelector variant="mobile" className="pt-3" />
                </nav>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

const aboutlinks = [
  {
    name: "About us",
    path: "/about_us",
  },
  {
    name: "Our Team",
    path: "/our_team",
  },
  {
    name: "Certificates",
    path: "/certificates",
  },
  {
    name: "Message from CEO",
    path: "/message_from_ceo",
  },
  {
    name: "Be Partner with us",
    path: "/partner",
  },
  {
    name: "Useful Information",
    path: "/useful_info/travel-guidelines-and-tips",
  },
  {
    name: "Terms & Conditions",
    path: "/terms",
  },
];
